import { UserType } from 'src/type/user';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
  // TimePicker
} from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Stack,
  Table,
  Dialog,
  Button,
  Divider,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  DialogTitle,
  ListItemText,
  DialogContent,
  DialogActions,
  InputAdornment,
  TableContainer,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import useProductOptions from 'src/hooks/use-product-options';
import { useBoolean } from 'src/hooks/use-boolean';
import useBranchOptions from 'src/hooks/use-branch-option';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect, useCallback } from 'react';
import { fNumber } from 'src/utils/format-number';
import useProductFeeOptions from 'src/hooks/use-product-fee-options';
import Iconify from 'src/components/iconify';
// import { uploadImage } from 'src/libs/firebase/firebase-upload';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios, { endpoints } from 'src/utils/axios';
import UserSearchAutocomplete, { UserOption } from 'src/components/ui-kit/user-search-autocomplete';
import {
  // RedeemTxType,
  GoldWithdrawFilters,
  GoldWithdrawFilterKey,
  GoldWithdrawFilterValue,
} from 'src/type/gold-withdraw';
import { convertGold } from 'src/utils/convert';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const defaultFilters: GoldWithdrawFilters = {
  name: null,
  productId: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

const defaultValues = {
  productId: '',
  amount: 0,
  branchId: '',
  appointmentDate: null as unknown as Dayjs,
  appointmentTime: null as unknown as Dayjs,
  // paymentImage: null as unknown as File,
  // paidAtDate: null as unknown as Dayjs,
  // paidAtTime: null as unknown as Dayjs,
  productRedeemFees: [{ id: '', amount: 0 }],
  unitType: 'BAHT',
};

const Schema = Yup.object().shape({
  productId: Yup.string().required('กรุณาเลือกสินค้า'),
  amount: Yup.number()
    .typeError('น้ำหนักต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุน้ำหนัก')
    .moreThan(-1, 'น้ำหนักขั้นต่ำ 0'),
  branchId: Yup.string().required('กรุณาเลือกสาขา'),
  appointmentDate: Yup.mixed<Dayjs>().required('กรุณาเลือกวันนัดหมาย'),
  appointmentTime: Yup.mixed<Dayjs>().required('กรุณาเลือกเวลานัดหมาย'),
  // paymentImage: Yup.mixed<File>().required('กรุณาแนบหลักฐานการชำระเงิน'),
  // paidAtDate: Yup.mixed<Dayjs>().required('กรุณากรอกวันที่โอน'),
  // paidAtTime: Yup.mixed<Dayjs>().required('กรุณากรอกเวลาที่โอน'),
  productRedeemFees: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required('จำเป็นต้องระบุค่าธรรมเนียม'),
      amount: Yup.number()
        .typeError('จำนวนต้องเป็นตัวเลข')
        .required('จำเป็นต้องระบุจำนวน')
        .moreThan(0, 'จำนวนขั้นต่ำ 1'),
    })
  ),
  unitType: Yup.string().required('กรุณาเลือกหน่วยของทองคำ'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType | null;
  isCreateAndApprove?: boolean;
  handleReload: VoidFunction;
  openApproveDialog?: (txId?: string) => void;
};

export default function RedeemCreateDialog({
  open,
  onClose,
  user,
  isCreateAndApprove = false,
  handleReload,
  openApproveDialog,
}: Props) {
  const currentDateTime = useCurrentDateTime();
  const [filters, setFilters] = useState(defaultFilters);

  const amountError = useBoolean();
  const isDevSubmitting = useBoolean(false);
  const isDevSubmitting2 = useBoolean(false);

  const handleFilters = useCallback(
    (name: GoldWithdrawFilterKey, value: GoldWithdrawFilterValue) => {
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleFilterName = useCallback(
    (userOption: UserOption | null) => {
      handleFilters('name', userOption);
    },
    [handleFilters]
  );

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    control,
    watch,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productRedeemFees',
  });

  const handleAdd = () => {
    append({
      id: '',
      amount: 0,
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (watch().amount && watch().productId) {
      const product = user?.userProducts?.find((pro) => pro.productId === watch().productId);

      if (
        Number(watch().amount) >
        (watch().unitType === 'BAHT'
          ? Number(product?.amount!)
          : convertGold(Number(product?.amount!), 'weightGrams'))
      ) {
        amountError.onTrue();
      } else {
        amountError.onFalse();
      }
    }
  }, [watch().amount, watch().productId]);

  const { productOptions } = useProductOptions(true, user?.uid);
  const selectedProduct = productOptions.find((item) => item.value === watch().productId);
  const isBar96 =
    selectedProduct &&
    selectedProduct?.label.includes('แท่ง') &&
    selectedProduct?.label.includes('96.5');

  const { branchOptions } = useBranchOptions();
  const { productFeeOptions } = useProductFeeOptions(
    watch().productId,
    watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (totalWeight !== Number(values.amount)) {
        throw new Error('ขนาดทองไม่ตรงกับจำนวนทองที่ต้องการแลกเปลี่ยน');
      }

      // const paymentImageUrl = await uploadImage(values.paymentImage, 'images');

      // values.paidAtDate
      //   .set('hour', values.paidAtTime.hour())
      //   .set('minute', values.paidAtTime.minute());

      const combinedDateTime = dayjs(values.appointmentDate)
        .set('hour', dayjs(values.appointmentTime).hour())
        .set('minute', dayjs(values.appointmentTime).minute());

      const uid = user?.uid || filters.name?.value;

      if (!uid) {
        throw new Error('ไม่พบข้อมูลลูกค้า');
      }

      const body = {
        uid,
        productId: values.productId,
        amount: Number(values.amount),
        branchId: values.branchId,
        appointmentDate: combinedDateTime.toISOString(),
        // paymentImageUrl,
        // paidAt: values.paidAtDate.toISOString(),
        productRedeemFees: values.productRedeemFees,
        currentDateTime,
        unitType: values.unitType,
      };

      const { data } = await axios.post(endpoints.transactions.redeem.create, body);

      if (isCreateAndApprove && openApproveDialog && data) {
        openApproveDialog(data.id);
      }

      if (!isCreateAndApprove) {
        handleReload();
        enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      }

      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  // const handleDevRedeem = async () => {
  //   try {
  //     isDevSubmitting.onTrue();

  //     // TODO: remove this after testing

  //     const body = {
  //       uid: 'rS3iMKwNOiYMzr6e2MV0m9fbD6k2',
  //       productId: '9FhTZkU6ssH60tPIcB3k',
  //       amount: 3,
  //       branchId: 'pYlTiyrmrClzZn1vzFdT',
  //       appointmentDate: '2026-01-11T01:28:51.379Z',
  //       productRedeemFees: [
  //         {
  //           id: 'pnpxcnwAEl6kuirDxpv2',
  //           amount: 3,
  //         },
  //       ],
  //       currentDateTime,
  //       unitType: 'BAHT',
  //     };

  //     // Create redeem transaction
  //     await axios.post<{ id: string } | RedeemTxType>(endpoints.transactions.redeem.create, body);

  //     handleReload();
  //     beforeClose();
  //     enqueueSnackbar('สร้างรายการสำเร็จ (Dev)', { variant: 'success' });
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     isDevSubmitting.onFalse();
  //   }
  // };

  // const handleDevRedeem2 = async () => {
  //   try {
  //     isDevSubmitting2.onTrue();

  //     // TODO: remove this after testing

  //     const body = {
  //       uid: 'rS3iMKwNOiYMzr6e2MV0m9fbD6k2',
  //       productId: '9FhTZkU6ssH60tPIcB3k',
  //       amount: 2,
  //       branchId: 'pYlTiyrmrClzZn1vzFdT',
  //       appointmentDate: '2026-01-11T01:35:56.718Z',
  //       productRedeemFees: [
  //         {
  //           id: 'J4kTlsi42pqyovlHnTmD',
  //           amount: 1,
  //         },
  //       ],
  //       currentDateTime,
  //       unitType: 'BAHT',
  //     };

  //     // Create redeem transaction
  //     await axios.post<{ id: string } | RedeemTxType>(endpoints.transactions.redeem.create, body);

  //     handleReload();
  //     beforeClose();
  //     enqueueSnackbar('สร้างรายการสำเร็จ (Dev)', { variant: 'success' });
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     isDevSubmitting2.onFalse();
  //   }
  // };

  // const handleDropImage = useCallback(
  //   (acceptedFiles: File[]) => {
  //     const file = acceptedFiles[0];

  //     const newFile = Object.assign(file, {
  //       preview: URL.createObjectURL(file),
  //     });

  //     if (file) {
  //       setValue('paymentImage', newFile, { shouldValidate: true });
  //     }
  //   },
  //   [setValue]
  // );

  const feeWithInfo = watch().productRedeemFees?.map((fee) => {
    const redeemFee = productFeeOptions.find((option) => option.value === fee.id)?.info;
    const weight =
      watch().unitType === 'BAHT' ? redeemFee?.weightBaht || 0 : redeemFee?.weightGram || 0;
    return {
      ...fee,
      weight,
    };
  });

  const totalWeight = feeWithInfo?.reduce((sum, item) => sum + item.amount * item.weight, 0) || 0;

  useEffect(() => {
    if (!isBar96) {
      setValue('unitType', 'GRAM');
    }
  }, [isBar96, setValue]);

  useEffect(() => {
    if (open) {
      // Set default productId to product with "แท่ง 96.5" if available and form is empty
      if (productOptions.length > 0 && !getValues('productId')) {
        const bar96Product = productOptions.find(
          (option) => option.label.includes('แท่ง') && option.label.includes('96.5')
        );
        if (bar96Product) {
          setValue('productId', bar96Product.value);
          // Set unitType to "BAHT" for "แท่ง 96.5" product
          setValue('unitType', 'BAHT');
        } else {
          // If no "แท่ง 96.5" product found, use first product
          setValue('productId', productOptions[0].value);
        }
      }
      // Set default appointmentDate to current date if empty
      if (!getValues('appointmentDate')) {
        setValue('appointmentDate', dayjs() as unknown as Dayjs);
      }
      // Set default appointmentTime to current time if empty
      if (!getValues('appointmentTime')) {
        setValue('appointmentTime', dayjs() as unknown as Dayjs);
      }
      // Set default branchId to first option if available and form is empty
      if (branchOptions.length > 0 && !getValues('branchId')) {
        setValue('branchId', branchOptions[0].value);
      }
    }
  }, [open, productOptions, branchOptions, setValue, getValues]);

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างรายการถอนสินค้า</DialogTitle>

        <DialogContent>
          <Stack spacing={3} py={1}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {user?.userProducts?.map((product) => {
                    if (!product?.product?.name || product?.amount === 0) {
                      return null;
                    }

                    return (
                      <TableRow key={product.id}>
                        <TableCell
                          sx={{
                            fontWeight: watch().productId === product.productId ? 'bold' : '',
                            color: watch().productId !== product.productId ? 'text.secondary' : '',
                          }}
                        >
                          <Typography variant="body2" noWrap pb="21px">
                            {product?.product?.name}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: watch().productId === product.productId ? 'bold' : '',
                            color: watch().productId !== product.productId ? 'text.secondary' : '',
                          }}
                        >
                          <ListItemText
                            primary={`${fNumber(product?.amount)} กรัม`}
                            secondary={`≈ ${fNumber(convertGold(product?.amount, 'weightBaht'))} บาท`}
                            primaryTypographyProps={{
                              typography: 'body2',
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{
                              mt: 0.5,
                              component: 'span',
                              typography: 'caption',
                              noWrap: true,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {(!user || user === null) && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexGrow={1}
                sx={{ width: { xs: 1, sm: 'auto' }, minWidth: 140 }}
              >
                <UserSearchAutocomplete
                  label="ค้นหาด้วยชื่อลูกค้า"
                  value={filters.name}
                  onChange={handleFilterName}
                />
              </Stack>
            )}

            <RHFSelect name="productId" label="สินค้า" placeholder="เลือกสินค้า">
              {productOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
              <RHFSelect name="unitType" label="หน่วยทองคำ" disabled={!isBar96}>
                <MenuItem value="BAHT">บาท</MenuItem>
                <MenuItem value="GRAM">กรัม</MenuItem>
              </RHFSelect>

              <RHFTextField
                name="amount"
                type="number"
                label="น้ำหนัก"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                error={amountError.value}
                helperText={amountError.value ? 'น้ำหนักเกินกว่าที่สามารถทำรายการได้' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color="success.main">
                        {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <RHFSelect name="branchId" label="สาขา" placeholder="เลือกสาขา">
              {branchOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Controller
                name="appointmentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="วันนัดหมาย"
                    value={getValues('appointmentDate')}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    sx={{ width: 1 }}
                    disablePast
                    format="DD/MM/YYYY"
                  />
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="appointmentTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เวลานัดหมาย"
                      value={getValues('appointmentTime')}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ width: 1 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>

            <Stack spacing={3}>
              {/* <RHFUploadBox
                name="paymentImage"
                maxSize={3145728}
                onDrop={handleDropImage}
                placeholder="อัพโหลดรูปหลักฐาน"
                sx={{
                  maxHeight: 140,
                  width: '100%',
                  height: '100%',
                  aspectRatio: '1/1',
                  mx: 'auto',
                }}
                helperText={
                  <>
                    อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                    <br /> ขนาดสูงสุด {fData(3145728)}
                  </>
                }
              /> */}

              {/* <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Stack>
                    <Controller
                      name="paidAtDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          label="วันที่โอน"
                          value={getValues('paidAtDate')}
                          onChange={(newValue) => {
                            field.onChange(newValue);
                          }}
                        />
                      )}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="paidAtTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        label="เวลาที่โอน"
                        ampm={false}
                        format="HH:mm"
                        value={getValues('paidAtTime')}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid> */}

              <Stack spacing={3}>
                <Typography variant="caption" color="action.disabled">
                  ขนาดทอง
                </Typography>
                <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={2}>
                  {fields.map((item, index) => (
                    <Stack key={item.id} spacing={1.5} alignItems="flex-end">
                      <Grid container spacing={2} sx={{ width: 1 }}>
                        <Grid item xs={12} sm={8}>
                          <RHFSelect
                            size="small"
                            name={`productRedeemFees[${index}].id`}
                            label="ทอง"
                            InputLabelProps={{ shrink: true }}
                            placeholder="ทอง"
                            disabled={!watch().productId}
                            fullWidth
                          >
                            {productFeeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </RHFSelect>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                          >
                            <RHFTextField
                              size="small"
                              name={`productRedeemFees[${index}].amount`}
                              type="number"
                              label="จำนวน"
                              inputMode="numeric"
                              placeholder="0"
                              InputLabelProps={{ shrink: true }}
                              disabled={!watch().productId}
                              fullWidth
                              sx={{ flex: { xs: 1, sm: '1 1 auto' } }}
                            />
                            {fields.length > 1 && (
                              <Button
                                size="small"
                                color="error"
                                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                onClick={() => handleRemove(index)}
                                sx={{
                                  mt: { xs: 0, sm: 0.5 },
                                  width: { xs: '100%', sm: 'auto' },
                                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                }}
                              >
                                ลบ
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  ))}
                </Stack>

                <Stack spacing={3} direction="row" justifyContent="space-between">
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={handleAdd}
                    sx={{ flexShrink: 0 }}
                    disabled={!watch().productId}
                  >
                    เพิ่ม
                  </Button>

                  <Stack alignItems="flex-end" sx={{ width: 1 }}>
                    <Typography
                      variant={totalWeight !== Number(watch().amount) ? 'subtitle1' : 'subtitle2'}
                      color={totalWeight !== Number(watch().amount) ? 'error' : 'success.main'}
                    >
                      น้ำหนักทองรวม {fNumber(totalWeight)}{' '}
                      {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                    </Typography>
                    <Typography variant="subtitle2">
                      น้ำหนักทองที่ถอน {fNumber(Number(watch().amount))}{' '}
                      {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider flexItem sx={{ borderStyle: 'dashed' }} />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          {/* <LoadingButton
            variant="contained"
            onClick={handleDevRedeem}
            loading={isDevSubmitting.value}
            disabled={isSubmitting || isDevSubmitting.value || isDevSubmitting2.value}
            sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
          >
            dev-1bx3
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleDevRedeem2}
            loading={isDevSubmitting2.value}
            disabled={isSubmitting || isDevSubmitting.value || isDevSubmitting2.value}
            sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
          >
            dev-2bx1
          </LoadingButton> */}
          <Button
            variant="outlined"
            onClick={beforeClose}
            disabled={isSubmitting || isDevSubmitting.value || isDevSubmitting2.value}
          >
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={
              (isCreateAndApprove && !filters.name?.value && !user?.uid) ||
              isDevSubmitting.value ||
              isDevSubmitting2.value
            }
            loading={isSubmitting}
          >
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
